import React, { Suspense, useEffect, useState } from 'react';
import OrdersList from './OrdersList';
import ProductRequestPage from './ProductRequestPage';
import LowStockOrderForm from './LowStockOrderForm';
import { useAuth } from '../AuthContext';
import { isBrickFactoryUser, isAutomotiveBusiness } from '../utils/businessType';
import './OrdersPage.css';

const FactoryMaterialOrderForm = React.lazy(() => import('./FactoryMaterialOrderForm'));

/**
 * @param {'make'|'list'} [initialTab]
 * @param {'order'|'tracking'} [factorySection] factory-only: dedicated page without tabs
 */
export default function OrdersPage({ initialTab = 'make', onNavigate, factorySection }) {
  const { user } = useAuth();
  const factoryMode = isBrickFactoryUser(user);
  const automotiveMode = isAutomotiveBusiness(user);
  const catalogOrderMode = factoryMode || automotiveMode;
  const [tab, setTab] = useState(initialTab);
  const [orderType, setOrderType] = useState(factoryMode ? 'catalog' : 'new');

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    setOrderType(factoryMode ? 'catalog' : 'new');
  }, [factoryMode]);

  if (factoryMode && factorySection === 'order') {
    return (
      <div className="orders-page">
        <header className="orders-page-header">
          <h1 className="orders-page-title">Order materials</h1>
          <p className="orders-page-sub">
            Select a saved supplier and materials from your catalog. Set purchase price here; selling price when
            receiving delivery.
          </p>
        </header>
        <div className="orders-make-panel">
          <Suspense fallback={<p className="orders-page-loading">Loading order form…</p>}>
            <FactoryMaterialOrderForm onNavigate={onNavigate} />
          </Suspense>
        </div>
      </div>
    );
  }

  if (catalogOrderMode && factorySection === 'tracking') {
    return (
      <div className="orders-page page-bleed">
        <header className="orders-page-header">
          <h1 className="orders-page-title">
            {automotiveMode ? 'Receive & tracking' : 'Tracking status'}
          </h1>
          <p className="orders-page-sub">
            {automotiveMode
              ? 'Fuatilia purchase orders kutoka kuwasilishwa hadi kukubaliwa, delivery, na kupokea mzigo.'
              : 'Follow material orders from submission through approval, delivery, and receive.'}
          </p>
        </header>
        <OrdersList
          factoryTracking={factoryMode}
          automotiveTracking={automotiveMode}
          onNavigate={onNavigate}
        />
      </div>
    );
  }

  return (
    <div className="orders-page">
      <header className="orders-page-header">
        <h1 className="orders-page-title">
          {factoryMode ? 'Material orders' : 'Purchase Orders'}
        </h1>
        <p className="orders-page-sub">
          {factoryMode
            ? 'Order only from raw materials in your catalog (Raw materials menu). Purchase price here; selling price on receive.'
            : 'Prepare supplier-ready stock orders. Staff draft requests, and owners review and approve before purchase.'}
        </p>
      </header>

      <div className="orders-tabs" role="tablist" aria-label="Orders sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'make'}
          className={`orders-tab${tab === 'make' ? ' orders-tab--active' : ''}`}
          onClick={() => setTab('make')}
        >
          {factoryMode ? 'New material order' : 'Create PO'}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'list'}
          className={`orders-tab${tab === 'list' ? ' orders-tab--active' : ''}`}
          onClick={() => setTab('list')}
        >
          {factoryMode ? 'Order list' : 'PO list'}
        </button>
      </div>

      {tab === 'make' && (
        <div className="orders-make-panel">
          {factoryMode ? (
            <Suspense fallback={<p className="orders-page-loading">Loading order form…</p>}>
              <FactoryMaterialOrderForm onNavigate={onNavigate} />
            </Suspense>
          ) : (
            <>
              <div className="orders-type-tabs" role="tablist" aria-label="Request type">
                <button
                  type="button"
                  className={`orders-type-tab${orderType === 'lowstock' ? ' orders-type-tab--active' : ''}`}
                  onClick={() => setOrderType('lowstock')}
                >
                  Reorder low stock
                </button>
                <button
                  type="button"
                  className={`orders-type-tab${orderType === 'new' ? ' orders-type-tab--active' : ''}`}
                  onClick={() => setOrderType('new')}
                >
                  Custom PO
                </button>
              </div>
              {orderType === 'lowstock' ? <LowStockOrderForm /> : <ProductRequestPage embedded />}
            </>
          )}
        </div>
      )}

      {tab === 'list' && <OrdersList factoryTracking={factoryMode} onNavigate={onNavigate} />}
    </div>
  );
}

